import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// "My briefs" data layer: delivered outputs (self-gating view), the daily digest
// (prebriefs, latest per surface), and the feedback writer (the UI is the first writer).

type Row = Record<string, any>;
// The delivered-outputs view and prebriefs table are read permissively; cast the client so
// a name the generated types might not surface still compiles. RLS does the row scoping.
const sb = supabase as any;

export interface DeliveredOutput extends Row {
  id?: string;
  title?: string;
  output_kind?: string;
  sent_at?: string;
  asana_permalink?: string | null;
  send_error?: string | null;
}
export function useDeliveredBriefs() {
  return useQuery({
    queryKey: ["v_research_delivered_outputs"],
    staleTime: 60 * 1000,
    queryFn: async () => {
      // Self-gating in SQL — do NOT add a surface filter.
      const { data, error } = await sb
        .from("v_research_delivered_outputs")
        .select("*")
        .order("sent_at", { ascending: false });
      if (error) throw error;
      return ((data as DeliveredOutput[]) ?? []).filter(Boolean);
    },
  });
}

export interface RolePrebrief extends Row {
  surface?: string;
  target_date?: string;
  digest?: Row;
  digest_markdown?: string;
  has_signal?: boolean;
  generated_at?: string;
}
// Rows are not deduped — take the latest per surface for today.
export function useRolePrebriefs() {
  return useQuery({
    queryKey: ["research_role_prebriefs", "today"],
    staleTime: 60 * 1000,
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      const { data, error } = await sb
        .from("research_role_prebriefs")
        .select("surface,target_date,digest,digest_markdown,has_signal,generated_at")
        .eq("target_date", today)
        .order("generated_at", { ascending: false });
      if (error) throw error;
      const rows = ((data as RolePrebrief[]) ?? []).filter(Boolean);
      const latestPerSurface = new Map<string, RolePrebrief>();
      for (const r of rows) {
        const key = r.surface ?? "default";
        if (!latestPerSurface.has(key)) latestPerSurface.set(key, r);
      }
      return Array.from(latestPerSurface.values());
    },
  });
}

export type FeedbackSignal = "thumb_up" | "thumb_down" | "edit" | "comment";
const VALID_SIGNALS: FeedbackSignal[] = ["thumb_up", "thumb_down", "edit", "comment"];

export function useRecordFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      draftId: string;
      signal: FeedbackSignal;
      rating?: number | null;
      editText?: string | null;
      comment?: string | null;
    }) => {
      // Validate client-side — the enum is strict and a bad value raises a Postgres error.
      if (!VALID_SIGNALS.includes(input.signal)) throw new Error("invalid_signal");
      if (input.rating != null && (input.rating < 1 || input.rating > 5)) throw new Error("rating_out_of_range");
      const { data, error } = await supabase.rpc("research_record_output_feedback", {
        _draft_id: input.draftId,
        _signal: input.signal,
        _rating: input.rating ?? null,
        _edit_text: input.editText ?? null,
        _comment: input.comment ?? null,
      });
      if (error) throw error;
      return data; // new feedback row id — never displayed
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["v_research_delivered_outputs"] });
    },
  });
}
